/**
 * CpLeftRail.tsx — CP1-CP10 picker for the Answer Lane
 *
 * W21: Primary Answer Surface
 *
 * Left rail (240px desktop) — collapses to top horizontal strip on tablet/mobile.
 * Shows gate-state dot, answered/total counts, percent bar.
 * Active CP gets left border accent #0B0B0F.
 *
 * Canon palette: gray #9CA3AF, red #DC2626, orange #EA580C, yellow #EAB308, green #16A34A
 * Canon CP names: CP1 — Project (5%) ... CP10 — Legal (2%)
 */

import type { CpProgress, GateStatus } from "@/hooks/useAnswerLane";
import { CP_SHORT_NAMES, GATE_COLORS, CP_WEIGHTS } from "@/hooks/useAnswerLane";

interface Props {
  cpProgress: CpProgress[];
  activeCp: number;
  onSelectCp: (cp: number) => void;
  accountName?: string;
  compositeScore?: number;
  /** When true, renders as a horizontal chip row for tablet/mobile */
  horizontal?: boolean;
}

function gateColor(status: string): string {
  const s = (status || "").toLowerCase();
  if (s === "green") return GATE_COLORS.green;
  if (s === "yellow") return GATE_COLORS.yellow;
  if (s === "orange") return GATE_COLORS.orange;
  if (s === "red") return GATE_COLORS.red;
  return GATE_COLORS.gray;
}

export default function CpLeftRail({
  cpProgress,
  activeCp,
  onSelectCp,
  accountName,
  compositeScore,
  horizontal = false,
}: Props) {
  // Build a map of cp_number → progress
  const progressMap = new Map<number, CpProgress>();
  for (const p of cpProgress) {
    progressMap.set(p.cp_number, p);
  }

  const cps = Array.from({ length: 10 }, (_, i) => i + 1);

  if (horizontal) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 4,
          overflowX: "auto",
          padding: "8px 12px",
          borderBottom: "1px solid #E5E5E7",
          background: "#FAFAF7",
          scrollbarWidth: "none",
        }}
        role="tablist"
        aria-label="Checkpoint picker"
      >
        {cps.map((cp) => {
          const p = progressMap.get(cp);
          const color = gateColor(p?.status ?? "gray");
          const isActive = cp === activeCp;
          return (
            <button
              key={cp}
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelectCp(cp)}
              style={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 10px",
                borderRadius: 8,
                border: `1px solid ${isActive ? "#0B0B0F" : "#E5E5E7"}`,
                background: isActive ? "#0B0B0F" : "#FFFFFF",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? "#FFFFFF" : "#0B0B0F",
                transition: "all 0.12s",
                whiteSpace: "nowrap",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: color,
                  flexShrink: 0,
                }}
              />
              <span>CP{cp}</span>
              {p && (
                <span
                  style={{
                    fontSize: 10,
                    color: isActive ? "#D1D5DB" : "#6B6B70",
                    fontWeight: 400,
                  }}
                >
                  {p.answered}/{p.total}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <nav
      style={{
        width: 240,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        background: "#FAFAF7",
        borderRight: "1px solid #E5E5E7",
        overflowY: "auto",
        height: "100%",
      }}
      aria-label="Checkpoint navigation"
    >
      {/* Account header */}
      <div
        style={{
          padding: "16px 16px 12px",
          borderBottom: "1px solid #E5E5E7",
        }}
      >
        {accountName && (
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#0B0B0F",
              marginBottom: 6,
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {accountName}
          </div>
        )}
        {compositeScore !== undefined && compositeScore !== null && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: "#F4F4F5",
              borderRadius: 6,
              padding: "3px 8px",
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#6B6B70",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              Composite
            </span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: "#0B0B0F",
              }}
            >
              {Math.round(compositeScore)}
            </span>
          </div>
        )}
      </div>

      {/* CP rows */}
      <ul
        style={{ listStyle: "none", margin: 0, padding: "8px 0", flex: 1 }}
        role="list"
      >
        {cps.map((cp) => {
          const p = progressMap.get(cp);
          const color = gateColor(p?.status ?? "gray");
          const isActive = cp === activeCp;
          const answered = p?.answered ?? 0;
          const total = p?.total ?? 0;
          const percent = total > 0 ? Math.round((answered / total) * 100) : 0;
          const shortName = CP_SHORT_NAMES[cp] ?? `CP${cp}`;
          const weight = CP_WEIGHTS[cp] ?? 0;

          return (
            <li key={cp}>
              <button
                onClick={() => onSelectCp(cp)}
                aria-current={isActive ? "true" : undefined}
                style={{
                  width: "100%",
                  display: "block",
                  padding: "8px 16px 8px 14px",
                  background: isActive ? "#F4F4F5" : "transparent",
                  border: "none",
                  borderLeft: isActive ? "3px solid #0B0B0F" : "3px solid transparent",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 0.12s",
                }}
              >
                {/* Row top: dot + label + weight */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: color,
                      flexShrink: 0,
                    }}
                    aria-label={`Status: ${p?.status ?? "not started"}`}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: isActive ? 700 : 500,
                      color: "#0B0B0F",
                      flex: 1,
                      lineHeight: 1.3,
                    }}
                  >
                    {shortName}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: "#9CA3AF",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {weight}%
                  </span>
                </div>

                {/* Answered count */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 5,
                  }}
                >
                  <span style={{ fontSize: 10, color: "#6B6B70" }}>
                    {answered} of {total} answered
                  </span>
                  <span style={{ fontSize: 10, color: "#9CA3AF" }}>
                    {percent}%
                  </span>
                </div>

                {/* Progress bar */}
                <div
                  style={{
                    height: 3,
                    borderRadius: 2,
                    background: "#E5E5E7",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${percent}%`,
                      background: color,
                      borderRadius: 2,
                      transition: "width 0.3s",
                    }}
                  />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
